// ========================================
// Gratitude List Component
// ========================================
// Manage list of things you're grateful for

import { useState, KeyboardEvent } from 'react';
import { useGratitudes } from '../../hooks/useGratitudes';
import GratitudeItem from './GratitudeItem';
import './GratitudeResentment.css';

const GratitudeList = () => {
  const { gratitudes, loading, addGratitude } = useGratitudes();

  const [item, setItem] = useState('');
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!item.trim()) return;

    setError('');
    setSubmitting(true);

    try {
      await addGratitude(item.trim(), notes.trim());

      // Reset form
      setItem('');
      setNotes('');
      setShowNotes(false);
    } catch (err) {
      console.error('Error adding gratitude:', err);
      setError('Failed to add gratitude. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const activeGratitudes = gratitudes.filter((g) => g.isActive);
  const inactiveGratitudes = gratitudes.filter((g) => !g.isActive);

  if (loading) {
    return (
      <div className="list-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading gratitudes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="list-container">
      <div className="list-content">
        {/* Header */}
        <div className="list-header glass-panel">
          <div className="header-content">
            <h1>Gratitude List</h1>
            <p>Track and celebrate what you're grateful for</p>
          </div>
        </div>

        {/* Quick Add Input */}
        <div className="quick-add-container">
          {error && (
            <div className="error-message" role="alert" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <div className="quick-add-wrapper">
            <input
              type="text"
              value={item}
              onChange={(e) => setItem(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="What are you grateful for today?"
              className="quick-add-input"
              disabled={submitting}
              autoFocus
            />

            <div className="quick-add-actions">
              <button
                className={`icon-btn ${showNotes ? 'active' : ''}`}
                onClick={() => setShowNotes(!showNotes)}
                title="Add notes"
                type="button"
              >
                📝
              </button>
              <button
                className="icon-btn"
                onClick={handleSubmit}
                disabled={!item.trim() || submitting}
                title="Add gratitude"
                type="button"
              >
                ↵
              </button>
            </div>
          </div>

          {showNotes && (
            <div className="quick-add-notes">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional context or thoughts..."
                className="notes-textarea"
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Gratitudes List */}
        {gratitudes.length === 0 ? (
          <div className="empty-state glass-panel">
            <h2>Start Your Gratitude Practice</h2>
            <p>Add things you're grateful for to build a positive mindset and track what brings you joy.</p>
          </div>
        ) : (
          <>
            {activeGratitudes.length > 0 && (
              <section className="list-section">
                <h2 className="section-title">Active Gratitudes</h2>
                <div className="items-grid">
                  {activeGratitudes.map((gratitude) => (
                    <GratitudeItem key={gratitude.id} gratitude={gratitude} />
                  ))}
                </div>
              </section>
            )}

            {inactiveGratitudes.length > 0 && (
              <section className="list-section">
                <h2 className="section-title">Archived</h2>
                <div className="items-grid">
                  {inactiveGratitudes.map((gratitude) => (
                    <GratitudeItem key={gratitude.id} gratitude={gratitude} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GratitudeList;
